import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Slack, Loader2, Send, Trash2, ExternalLink,
  CheckCircle2, AlertCircle, Save, Hash,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OrgSlackSettings — per-org Slack-channel wiring for cost-anomaly alerts.
 *
 * Lets an org admin paste their own incoming-webhook URL so anomaly
 * alerts hit *their* channel, instead of the founder-global env webhook.
 * The URL is masked on read; only ever shown plaintext when the admin
 * is mid-paste. Channel hint (e.g. "#finops-alerts") is purely cosmetic.
 */
const OrgSlackSettings = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [channelHint, setChannelHint] = useState('');

  const _auth = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/org-settings/slack`, { headers: _auth() });
      setState(r.data);
      if (r.data?.channel_hint) setChannelHint(r.data.channel_hint);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Slack settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!webhookUrl.startsWith('https://hooks.slack.com/services/')) {
      toast.error('URL must start with https://hooks.slack.com/services/');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/org-settings/slack`,
        { webhook_url: webhookUrl.trim(), channel_hint: channelHint.trim() || null },
        { headers: _auth() }
      );
      setState(r.data);
      setWebhookUrl('');
      toast.success('Webhook saved · channel wired for cost anomaly alerts');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save webhook');
    } finally {
      setSaving(false);
    }
  };

  const sendPing = async () => {
    setPinging(true);
    try {
      const r = await axios.post(
        `${API}/org-settings/slack/test`,
        {},
        { headers: _auth() }
      );
      if (r.data?.sent) {
        toast.success('Test ping delivered to your Slack channel');
      } else {
        toast.error(`Slack returned: ${r.data?.status || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send test ping');
    } finally {
      setPinging(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the configured Slack webhook? Cost anomaly alerts will fall back to email-only.')) return;
    setRemoving(true);
    try {
      await axios.delete(`${API}/org-settings/slack`, { headers: _auth() });
      setState({ configured: false, webhook_url_masked: null, channel_hint: null });
      setChannelHint('');
      toast.success('Webhook removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove webhook');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-300 mr-2" />
        <span className="text-slate-400 text-sm">Loading Slack settings…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 px-4 sm:px-6 py-12" data-testid="org-slack-settings-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 flex items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900 tracking-tight flex items-center gap-2" data-testid="org-slack-page-title">
              <Slack className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Slack Notifications</BrandTitle>
            </h1>
            <p className="text-sm text-slate-700 mt-1.5 max-w-2xl">
              Pipe cost-anomaly alerts and critical notifications straight into
              your team's Slack channel — no email forwarding, no shared inboxes.
            </p>
          </div>
        </div>

        {/* Status banner */}
        <Card
          data-testid="org-slack-status-banner"
          className={`mb-6 border ${
            state?.configured
              ? 'bg-slate-900 border-emerald-500/30'
              : 'bg-slate-900 text-slate-100 border-slate-800'
          }`}
        >
          <CardContent className="p-5 flex items-center gap-4 flex-wrap">
            {state?.configured ? (
              <>
                <CheckCircle2 className="w-7 h-7 text-emerald-300 shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-emerald-200" data-testid="org-slack-status-text">
                      Webhook configured
                    </span>
                    <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40 text-[11px] tracking-widest">
                      ACTIVE
                    </Badge>
                  </div>
                  <div className="text-[12px] text-slate-400 font-mono truncate" data-testid="org-slack-masked-url">
                    {state.webhook_url_masked}
                  </div>
                  {state.channel_hint && (
                    <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {state.channel_hint.replace(/^#/, '')}
                    </div>
                  )}
                  {state.updated_at && (
                    <div className="text-[11px] text-slate-400 mt-1">
                      Last changed {new Date(state.updated_at).toLocaleString()}
                      {state.updated_by ? ` · by ${state.updated_by}` : ''}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    data-testid="org-slack-test-ping-btn"
                    onClick={sendPing}
                    disabled={pinging}
                    variant="outline"
                    className="bg-slate-800 border-slate-700 hover:bg-slate-700 text-slate-300"
                  >
                    {pinging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send test ping
                  </Button>
                  <Button
                    data-testid="org-slack-remove-btn"
                    onClick={remove}
                    disabled={removing}
                    variant="outline"
                    className="bg-rose-500/10 border-rose-400/30 hover:bg-rose-500/20 text-rose-200"
                  >
                    {removing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Remove
                  </Button>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-7 h-7 text-amber-300 shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold text-amber-200">No webhook configured</div>
                  <div className="text-[12px] text-slate-400 mt-0.5">
                    Cost-anomaly alerts are currently email-only for this org. Wire a
                    Slack channel below to bring them into your team's flow.
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Configure form */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800">
          <CardContent className="p-6 space-y-5">
            <div>
              <Label htmlFor="webhook-url-input" className="text-sm font-semibold text-white">
                Slack Incoming Webhook URL
              </Label>
              <p className="text-[11px] text-slate-400 mt-1 mb-2">
                Generate one at{' '}
                <a
                  href="https://api.slack.com/apps"
                  target="_blank"
                  rel="noreferrer"
                  className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
                  data-testid="org-slack-help-link"
                >
                  api.slack.com/apps <ExternalLink className="w-3 h-3" />
                </a>{' '}
                → Create New App → Incoming Webhooks → Add to Workspace.
              </p>
              <Input
                id="webhook-url-input"
                data-testid="org-slack-webhook-input"
                type="url"
                placeholder="https://hooks.slack.com/services/T.../B.../..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="bg-slate-950 border-slate-700 text-white font-mono text-[12px]"
              />
            </div>
            <div>
              <Label htmlFor="channel-hint-input" className="text-sm font-semibold text-white">
                Channel hint <span className="text-slate-400 font-normal">(optional, cosmetic only)</span>
              </Label>
              <p className="text-[11px] text-slate-400 mt-1 mb-2">
                Just for your own reference — e.g. <code className="text-cyan-300">#finops-alerts</code>.
              </p>
              <Input
                id="channel-hint-input"
                data-testid="org-slack-channel-hint-input"
                type="text"
                placeholder="#finops-alerts"
                value={channelHint}
                onChange={(e) => setChannelHint(e.target.value)}
                className="bg-slate-950 border-slate-700 text-white"
                maxLength={80}
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                data-testid="org-slack-save-btn"
                onClick={save}
                disabled={saving || !webhookUrl}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold border-0"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {state?.configured ? 'Replace webhook' : 'Save webhook'}
              </Button>
              <p className="text-[11px] text-slate-400">
                URL is masked on read · only the suffix-hash is stored in plaintext.
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="mt-6 text-[11px] text-slate-400 text-center">
          What posts here? Cost-anomaly alerts (deviation &gt; threshold) and
          critical guard-rail breaches. Weekly cornerstone digests stay on the
          founder's global webhook.
        </p>
      </div>
    </div>
  );
};

export default OrgSlackSettings;
